import React, { useEffect, useRef, useState } from 'react';
import styles from './index.css';
import errorBoundary from '@ifeng-fe/errorBoundary';
import Observer from '@researchgate/react-intersection-observer';
import { formatSecond } from '@src/utils/index';
import { type Post } from '@src/services/interface';

type ShortVideoProps = {
    content?: {
        isFeed?: boolean;
        hasVideo?: Post['hasVideo'];
        video?: Post['video'];
    };
};

// 需在入口文件引入import { weixinIosVideoInit } from '@src/utils'; 并执行
const ShortVideo = ({ content }: ShortVideoProps) => {
    const duration = content?.video?.duration || 0;
    const [isfullScreenPlay, setfullScreenPlay] = useState<boolean>(false);
    const [isMute, setMute] = useState<boolean>(true);
    const [boxHeight, setBoxHeight] = useState<number>(0);
    const [fullScreenVideoHeight, setfullScreenVideoHeight] = useState<number>(0);
    const [videoTime, setVideoTime] = useState<string | number>(formatSecond(duration));
    const videoRef = useRef<HTMLVideoElement>(null);
    const videoRefBox = useRef<HTMLDivElement>(null);
    const isFeed = content?.isFeed || false;
    const posterImg = content?.video?.thumbnail || '';
    const videoUrl = content?.video?.url || '';
    const userAgent = navigator.userAgent.toLowerCase();
    const isIphone = userAgent.includes('iphone');
    const isWeixin = userAgent.includes('micromessenger');
    // 微信播放器初始化
    useEffect(() => {
        const videoDom = videoRef?.current;
        const videoRefBoxDom = videoRefBox?.current;
        const pageWidth = window.screen.width || document.body.offsetWidth;
        const wVideo = content?.video?.width || 0;
        const hVideo = content?.video?.height || 0;

        if (isWeixin && videoDom && isIphone) {
            videoDom?.play();

            videoDom.addEventListener('timeupdate', () => {
                if (videoDom.currentTime && duration) {
                    const diffTime = Math.trunc(duration) - Math.trunc(videoDom.currentTime);

                    setVideoTime(formatSecond(diffTime));
                }
            });

            videoDom.addEventListener('ended', () => {
                setVideoTime(formatSecond(duration));
            });
        }

        if (videoRefBoxDom && wVideo && hVideo) {
            console.log(wVideo, hVideo);
            const videoWidth = videoRefBoxDom.offsetWidth;
            let videoHeight = 0;
            const aspectRatio = wVideo / hVideo;

            if (aspectRatio >= 16 / 9) {
                // 视频尺寸16:9
                videoHeight = (videoWidth * 9) / 16;
            } else if (aspectRatio < 16 / 9 && aspectRatio > 3 / 4) {
                // 视频尺寸原始尺寸比例
                videoHeight = (videoWidth * hVideo) / wVideo;
            } else if (aspectRatio <= 3 / 4) {
                // 视频尺寸3:4
                videoHeight = (videoWidth * 4) / 3;
            }

            const fullVideoHeight = (pageWidth * hVideo) / wVideo;

            setBoxHeight(videoHeight);
            setfullScreenVideoHeight(fullVideoHeight);
        }
    }, [content, isIphone, isWeixin, duration]);

    // 全屏播放操作
    useEffect(() => {
        const videoDom = videoRef?.current;

        if (isfullScreenPlay) {
            videoDom?.play();
        } else {
            videoDom?.pause();
        }
    }, [isfullScreenPlay]);

    const handleChange = (event: any) => {
        const videoDom = videoRef?.current;

        if (isWeixin && videoDom && isIphone) {
            if (event.isIntersecting) {
                try {
                    videoDom?.play();
                    WeixinJSBridge.invoke('getNetworkType', {}, () => {
                        videoDom?.play();
                    });
                } catch (e) {
                    // console.error(e);
                }
            } else {
                videoDom?.pause();
            }
        }
    };

    const openFullScreenPlay = () => {
        setfullScreenPlay(true);
    };

    const closeFullScreenHandle = () => {
        setfullScreenPlay(false);
        setMute(true);
    };

    const muteHandle = () => {
        setMute(!isMute);

        const videoDom = videoRef?.current;
        if (videoDom) {
            videoDom?.play();
        }
    };

    const fullScreenVideoStyle = fullScreenVideoHeight
        ? {
              height: `${fullScreenVideoHeight}px`,
          }
        : {};

    return content?.hasVideo ? (
        <>
            {isWeixin && isIphone ? (
                <Observer onChange={handleChange}>
                    <div
                        ref={videoRefBox}
                        style={{ height: boxHeight ? `${boxHeight}px` : '' }}
                        className={isFeed ? styles.feedShortVideo : styles.articleShortVideo}>
                        <div className={isMute ? styles.mute : styles.noMute} onClick={muteHandle} />
                        <div className={styles.playTime} onClick={openFullScreenPlay}>
                            {videoTime}
                        </div>
                        <video
                            onClick={openFullScreenPlay}
                            ref={videoRef}
                            playsInline={true}
                            x5-playsinline="true"
                            webkit-playsinline="true"
                            className={styles.videoBox}
                            src={videoUrl}
                            loop={false}
                            controls={false}
                            muted={isMute}
                            preload="auto"
                            poster={posterImg}
                            x5-video-player-type="h5"
                        />
                    </div>
                </Observer>
            ) : (
                <div
                    ref={videoRefBox}
                    style={{ height: boxHeight ? `${boxHeight}px` : '' }}
                    className={isFeed ? styles.feedShortVideo : styles.articleShortVideo}>
                    <video
                        onClick={openFullScreenPlay}
                        ref={videoRef}
                        playsInline={true}
                        x5-playsinline="true"
                        webkit-playsinline="true"
                        className={styles.videoBox}
                        src={videoUrl}
                        loop={false}
                        controls={false}
                        muted={isMute}
                        preload="auto"
                        poster={posterImg}
                        x5-video-player-type="h5"
                    />
                    {/* <LazyImg className={styles.poster} src={posterImg} onClick={openFullScreenPlay} /> */}
                    <div className={styles.playIcon} onClick={openFullScreenPlay} />
                    <div className={styles.playTime} onClick={openFullScreenPlay}>
                        {videoTime}
                    </div>
                </div>
            )}
            {/* 不能采用fullscreen api的方式进行全屏控制，因为微信环境下不支持此API */}
            {isfullScreenPlay && (
                <div className={styles.fullScreen}>
                    <div className={styles.close} onClick={closeFullScreenHandle} />
                    <video
                        style={fullScreenVideoStyle}
                        ref={videoRef}
                        playsInline={true}
                        x5-playsinline="true"
                        webkit-playsinline="true"
                        className={styles.videoBox}
                        src={videoUrl}
                        controls={true}
                        preload="auto"
                        poster={posterImg}
                    />
                </div>
            )}
        </>
    ) : null;
};

export default errorBoundary(ShortVideo);
